/**
 * VersionPreview — render ONE chosen version and say plainly whether it is the one
 * live packages use.
 *
 * BACKLOG-1744 defect (2026-10-07): the old preview sent only the template code, and
 * the server picked "Active first, else newest" — so opening a Draft showed the
 * Active text (or vice versa) with no indication. Now the version id is always sent
 * and the server renders exactly that version, returning its status plus the id of
 * the live version for comparison.
 *
 * Route: POST /v3/admin/disclosures/preview { templateCode, versionId }.
 * No sample customer data is sent; merge fields that need a cart show up under
 * "Not filled in this preview".
 */

import { useCallback, useEffect, useState } from 'react';
import { Eye, RefreshCw } from 'lucide-react';

import {
  previewRender,
  type DisclosurePreviewResponse,
} from '@/services/disclosuresAdminApi';
import type { DisclosureVersionRow } from '@/services/disclosuresApi';

import { PillStatus } from './_shared';

export function VersionPreview({
  templateCode,
  version,
  autoRun,
}: {
  templateCode: string;
  version: DisclosureVersionRow;
  autoRun?: boolean;
}): JSX.Element {
  const [preview, setPreview] = useState<DisclosurePreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const versionId = Number(version.versionId ?? 0);

  const run = useCallback(async (): Promise<void> => {
    if (versionId <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const r = await previewRender({ templateCode, versionId, includePdf: false });
      if (!r.success) {
        setPreview(null);
        setError(r.error ?? 'Preview failed.');
        return;
      }
      setPreview(r.data ?? null);
    } finally {
      setBusy(false);
    }
  }, [templateCode, versionId]);

  useEffect(() => {
    setPreview(null);
    setError(null);
    if (autoRun) void run();
  }, [autoRun, run]);

  const d = preview?.diagnostics;
  const mismatch = preview && preview.versionId !== undefined && preview.versionId !== versionId;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void run()} disabled={busy || versionId <= 0}>
          {preview ? <RefreshCw size={12} /> : <Eye size={12} />} {busy ? 'Rendering…' : preview ? 'Render again' : 'Preview this version'}
        </button>
        {preview && (
          <>
            <span style={{ fontSize: 13 }}>
              Showing version <strong>{preview.versionNumber ?? '—'}</strong>
            </span>
            <PillStatus status={preview.versionStatus ?? null} />
            {preview.isLiveVersion ? (
              <span className="pill pill--ok">Used in live packages</span>
            ) : (
              <span className="pill pill--warn">
                Not used in live packages
                {preview.liveVersionNumber ? ` (live: version ${preview.liveVersionNumber})` : ' (no active version)'}
              </span>
            )}
            {preview.resolvedBodyTemplateCode && preview.resolvedBodyTemplateCode !== templateCode && (
              <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                Live packages swap this template&apos;s text for <code>{preview.resolvedBodyTemplateCode}</code> (variant).
              </span>
            )}
          </>
        )}
      </div>
      {error && (
        <div className="banner banner--err" role="alert" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}
      {mismatch && (
        <div className="banner banner--err" role="alert" style={{ marginTop: 8 }}>
          The server rendered version id {preview?.versionId}, not the one you opened ({versionId}). Do not rely on this preview.
        </div>
      )}
      {preview && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,3fr) minmax(0,2fr)', gap: 16, marginTop: 8 }}>
          <iframe
            title={`Preview of ${templateCode} version ${preview.versionNumber ?? ''}`}
            srcDoc={preview.mergedHtml ?? '<i>(nothing rendered)</i>'}
            sandbox=""
            style={{ width: '100%', minHeight: 520, border: '1px solid var(--ink-300)', borderRadius: 6, background: '#fff' }}
          />
          <div style={{ fontSize: 12 }}>
            {d?.renderError && (
              <div className="banner banner--err" style={{ marginBottom: 8 }}>
                {d.renderError}
              </div>
            )}
            <Diag label="Merge fields in the text" items={d?.referencedTokens ?? []} tone="info" />
            <Diag label="Filled" items={d?.resolvedTokens ?? []} tone="ok" />
            <Diag label="Not filled in this preview (optional)" items={d?.optionalMisses ?? []} tone="warn" />
            <Diag label="Not filled in this preview (required — a real package needs these)" items={d?.requiredMisses ?? []} tone="err" />
            {(d?.warnings?.length ?? 0) > 0 && <Diag label="Warnings" items={d?.warnings ?? []} tone="warn" />}
          </div>
        </div>
      )}
    </div>
  );
}

function Diag({
  label,
  items,
  tone,
}: {
  label: string;
  items: ReadonlyArray<string>;
  tone: 'ok' | 'warn' | 'err' | 'info';
}): JSX.Element {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontWeight: 600, color: 'var(--ink-600)', marginBottom: 4 }}>
        {label} ({items.length})
      </div>
      {items.length === 0 ? (
        <span style={{ color: 'var(--ink-500)' }}>None.</span>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {items.map((s) => (
            <span key={s} className={`pill pill--${tone}`} style={{ fontSize: 11 }}>
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
